import { defaultPostHeaderForJsonData, IAPIProxy } from '@inwink/apibase';
import type {
    IInWinkAdminEntityDataSource,
    IInwinkEntityQueryResult,
    IInwinkEntityUpdateOptions
} from '@inwink/apibase/inwinkdatasource';
import { Entities } from '@inwink/entities/entities';
import { expressionBuilder } from '@inwink/expressions/expressionbuilder';
import { convertFromOldFormat, explode } from '@inwink/expressions/converter';
import { IExpressionFilter, IExpressionBuilderPredicates } from '@inwink/expressions/expressionbuilder';
import { ISavePayloadEntityFormater } from '@@components/entities/formater/PayloadEntityFormaters';
import { combine } from '@inwink/utils/url';

export interface IInwinkEntityV3QuerySelects {
    [key: string]: boolean | IInwinkEntityV3QuerySelects;
}

export interface IInwinkEntityV3QueryPage {
    index?: number;
    size?: number;
    returnTotalCount?: boolean;
}

export interface IInwinkEntityV3QueryOrder {
    by: string;
    desc?: boolean;
    lang?: string;
}

export interface IInwinkEntityV3QueryOptions {
    page?: IInwinkEntityV3QueryPage;
    search?: string;
    order?: IInwinkEntityV3QueryOrder[];
    selects?: IInwinkEntityV3QuerySelects;
    filters?: IExpressionFilter;
    where?: IExpressionFilter;
    totalCount?: boolean;
    /**
     * @deprecated format v2
     */
    expression?: Entities.Expression | Entities.Expression[];
    queryString?: string;
    continuationTokenMode?: boolean;
    disableBaseFilters?: boolean;
    headers?: any;
}
export interface IInwinkEntityDataSourceOptions<T = any> {
    query?: IInwinkEntityV3QueryOptions;
    beforeCreate?: (entity: T) => any;
    beforeUpdate?: (entity: T) => any;
    additionnalColumns?: IDataSourceV3Column[];
}

export interface IDataSourceV3Options<T = any> extends IInwinkEntityDataSourceOptions<T> {
    forceDirectMassDelete?: boolean;
    forceDirectExport?: boolean;
    expandWorkTemplates?: boolean | Record<string, boolean>; // Records en case sensitive
    savePayloadFormater?: ISavePayloadEntityFormater;
    viewSuffix?: string;
    customWorkTemplateProviders?: Record<string, () => Promise<Entities.IEntityTemplateV3>>;
    viewFilters?: IExpressionBuilderPredicates;
}

export interface IDataSourceV3Column {
    key: string;
    titles?: Record<string, string>;
    type: string;
    isSelected?: boolean;
    orderable?: boolean;
    filterable?: boolean;
    valuesList?: any;
    expands?: string[];
}

export interface IImportResult<T = any> {
    entities: Array<IImportResultEntity<T>>;
    exceptionMessage?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface IImportResultEntity<T = any> {
    entityId?: any;
    entityIds?: any[];
    modelErrors?: IImportValidationError[];
    modelWarnings?: IImportValidationError[];
    validationErrors?: IImportValidationError[] | string[];
    exceptionMessage?: string;
}

export interface IImportPreviewResult<T = any> {
    entities: Array<IImportPreviewResultEntity<T>>;
}

export interface IImportPreviewResultEntity<T = any> {
    entity?: T;
    entities?: T[];
    modelErrors?: IImportValidationError[];
    modelWarnings?: IImportValidationError[];
    validationErrors?: IImportValidationError[] | string[];
}

export interface IImportValidationError {
    property?: string;
    code?: string;
    validationRuleKey?: string;
}

export interface IImportQuery<T = any> {
    filters?: IExpressionFilter;
    validations?: any;
    identifiers?: any;
    entities: T[];
    selects?: IInwinkEntityV3QuerySelects;
    arrayMergeKeys?: string[];
}

export interface IImportPreviewQuery<T = any> extends IImportQuery<T> {
}

export interface IInWinkAdminEntityV3DataSource<T> extends IInWinkAdminEntityDataSource<T> {
    count?: (options?: IInwinkEntityV3QueryOptions) => Promise<number>;
    query?: (options?: IInwinkEntityV3QueryOptions, expands?: any[]) => Promise<IInwinkEntityQueryResult<T>>;
}

export interface IExportV3Payload extends IInwinkEntityV3QueryOptions {
    columns: IExportV3Column[];
    expands: string[];
    title?: string;
}

export interface IPathPattern {
    $format: {
        $pattern: string,
        $values: any[]
    };
}
interface IExportFile {
    pathPattern: IPathPattern;
    selector: Record<string, any>;
    format?: string;
}

export interface IExportZipFilePayload extends IInwinkEntityV3QueryOptions {
    files: IExportFile[];
}

export interface IExportPdfFilePayload extends IInwinkEntityV3QueryOptions {
    title: string;
    fields: {
        selector: Record<string, any>;
    }[]
}
export interface IExportV3Column {
    label: string;
    selector: any;
}

export interface IMassUpdateRequest<T> {
    filters: any;
    input: Partial<T>;
}

export interface IDataSourceV3<T = any> extends Omit<IInWinkAdminEntityV3DataSource<T>, "import"> {
    entityName: string;
    options: IDataSourceV3Options<T>;
    customColumns: IDataSourceV3Column[];
    excludeColumns: string[];
    clone(newQueryOptions?: IDataSourceV3Options<T>): IDataSourceV3<T>;
    getAll(queryOptions?: IInwinkEntityV3QueryOptions, expands?): Promise<IInwinkEntityQueryResult<T>>;
    getById: (entityid) => Promise<T>;
    import: (query: IImportQuery<T>) => Promise<IImportResult<T>>;
    importPreview: (query: IImportPreviewQuery<T>) => Promise<IImportPreviewResult<T>>;
    worktemplate: (forceReload?: boolean, disableExpandWorkTemplates?: boolean) => Promise<Entities.IEntityTemplateV3>;
    exportV3: (query: IExportV3Payload, direct?: boolean) => Promise<any>;
    exportZipFile: (query: IExportZipFilePayload, direct?: boolean) => Promise<any>;
    exportPdfFile: (query: IExportPdfFilePayload, direct?: boolean) => Promise<any>;
    massUpdate: (input: Partial<T>, filters, direct?: boolean, populateWithMergeArray?: boolean) => Promise<any>;
    massDelete: (queryOptions?: IInwinkEntityV3QueryOptions, directDelete?: boolean) => Promise<IInwinkEntityQueryResult<T>>,
    getDocumentUrl: (entityid: string, templateid: string, lang: string) => Promise<any>;
    queryHistory?: (options?: IInwinkEntityV3QueryOptions, expands?: any[]) => Promise<IInwinkEntityQueryResult<T>>;
    queryRemoved?: (options?: IInwinkEntityV3QueryOptions, expands?: any[]) => Promise<IInwinkEntityQueryResult<T>>;
    countRemoved?: (options?: IInwinkEntityV3QueryOptions) => Promise<number>;
    queryCall?: (path: string, queryOptions?: IInwinkEntityV3QueryOptions, expands?) => Promise<IInwinkEntityQueryResult<T>>;
    entitytemplate?: (includeStaticFields: boolean, includeIdField?: boolean) => Promise<Entities.IEntityTemplate>;
}

export function getSelectsFromExpands(expands: string[], selects: IInwinkEntityV3QuerySelects = {}) {
    if (!!expands && expands.length > 0) {
        const arrayToObject = (_obj, arr, idx) => {
            const obj = _obj;
            const token = arr[idx];
            if (idx === arr.length - 1) {
                if (typeof obj[token] === "object") {
                    obj[token].$all = true;
                } else {
                    obj[token] = true;
                }
            } else {
                if (obj[token] === true) {
                    obj[token] = {};
                }
                obj[token] = obj[token] || {};
                const cur = obj[token];

                return arrayToObject(cur, arr, idx + 1);
            }
        };

        expands.forEach((x) => {
            arrayToObject(selects, x.split("."), 0);
        });
    }

    const selectsKeys = Object.keys(selects);
    if (selectsKeys.length === 0) {
        return null;
    }

    return selects;
}

interface IQueryArgs {
    filters: IExpressionFilter;
    selects: IInwinkEntityV3QuerySelects;
    orders: IQueryOrder[];
    page: IInwinkEntityV3QueryPage;
}

interface IQueryOrder {
    desc: boolean;
    value: any;
}

export function getFilters<T>(dataSourceOptions?: IDataSourceV3Options<T>, queryOptions?: IInwinkEntityV3QueryOptions) {
    const filterIsValid = (filter: IExpressionFilter) => {
        return !!filter && Object.keys(filter).length > 0;
    };
    const expressionIsValid = (expression: Entities.Expression | Entities.Expression[]) => {
        if (!expression) {
            return false;
        }
        if (Array.isArray(expression)) {
            return expression.length > 0;
        } else {
            return Object.keys(expression).length > 0;
        }
    };
    let filters: IExpressionFilter = queryOptions && queryOptions.where;
    const hasDefaultOptionsSetOnDataSource = dataSourceOptions && dataSourceOptions.query;

    if (queryOptions || hasDefaultOptionsSetOnDataSource) {
        const defaultFiltersOnDataSource = queryOptions?.disableBaseFilters != true ? dataSourceOptions?.query?.filters : null;
        const customFiltersOnQuery = queryOptions?.filters;
        const hasDefaultFiltersOnDataSource = filterIsValid(defaultFiltersOnDataSource);
        const hasCustomFiltersOnQuery = filterIsValid(customFiltersOnQuery);

        if (hasCustomFiltersOnQuery || hasDefaultFiltersOnDataSource) {
            if (hasCustomFiltersOnQuery && hasDefaultFiltersOnDataSource) {
                const queryFilterKeys = Object.keys(queryOptions.filters?.[0] || {});
                const dataSourceFilterKeys = Object.keys(dataSourceOptions.query.filters?.[0] || {});
                const hasConflictingFields = queryFilterKeys.some(key => dataSourceFilterKeys.includes(key));

                filters = expressionBuilder((e) => hasConflictingFields
                    ? e.FromFilter(dataSourceOptions.query.filters)
                    : e.And([
                        (_e) => _e.FromFilter(queryOptions.filters),
                        (_e) => _e.FromFilter(dataSourceOptions.query.filters)
                    ])
                );
            } else {
                const filtersSource = hasCustomFiltersOnQuery ? queryOptions.filters : dataSourceOptions.query.filters;
                filters = expressionBuilder((e) => e.FromFilter(filtersSource));
            }
        }
        const hasOldSyntaxQueryFilters = expressionIsValid(queryOptions?.expression);
        const hasOldSyntaxDataSourceFilters = expressionIsValid(dataSourceOptions?.query?.expression);
        if (hasOldSyntaxQueryFilters || hasOldSyntaxDataSourceFilters) {
            let f: Entities.Expression[] | Entities.Expression;
            if (hasOldSyntaxQueryFilters && hasOldSyntaxDataSourceFilters) {
                const queryExpr = Array.isArray(queryOptions.expression) ? queryOptions.expression : [queryOptions.expression];
                const dataSourceExpr = Array.isArray(dataSourceOptions.query.expression)
                    ? dataSourceOptions.query.expression : [dataSourceOptions.query.expression];
                f = { and: [...queryExpr, ...dataSourceExpr] };
            } else {
                f = hasOldSyntaxQueryFilters ? queryOptions.expression : dataSourceOptions?.query?.expression;
            }

            if (!Array.isArray(f)) {
                f = [f];
            }
            let oldFilter: IExpressionFilter;
            if (f && f.length && f[0].and) {
                oldFilter = convertFromOldFormat(f[0].and);
            } else if (f && f.length) {
                oldFilter = f && f.length && convertFromOldFormat(f);
            }

            if (filters) {
                if (oldFilter) {
                    filters = expressionBuilder((e) => e.And(
                        [
                            (_e) => _e.FromFilter(filters),
                            (_e) => _e.FromFilter(oldFilter)
                        ]
                    ));
                }
            } else if (oldFilter) {
                filters = expressionBuilder((e) => e.FromFilter(oldFilter));
            }
        }
    }

    if (filters && filters.$and && !filters.$and.length) {
        filters = null;
    }

    return filters;
}

function getOrderValue(order: IInwinkEntityV3QueryOrder) {
    let value: any;

    if (order.lang) {
        value = {
            $meta: {
                lang: order.lang,
                $token: {
                    [order.by]: {}
                }
            }
        };
    } else {
        value = explode(order.by, {});
    }

    return value;
}

function getQueryArgs<T>(dataSourceOptions?: IDataSourceV3Options<T>,
    queryOptions?: IInwinkEntityV3QueryOptions, expands?: string[]): Promise<IQueryArgs> {
    return new Promise((resolve) => {
        const filters = getFilters(dataSourceOptions, queryOptions);
        let page: IInwinkEntityV3QueryPage;
        let orders: IQueryOrder[];
        let selects: IInwinkEntityV3QuerySelects;

        const hasDefaultOptionsSetOnDataSource = dataSourceOptions?.query;

        if (queryOptions || hasDefaultOptionsSetOnDataSource) {
            if (queryOptions && queryOptions.page) {
                page = { index: queryOptions.page.index, size: queryOptions.page.size };
            } else if (dataSourceOptions?.query?.page) {
                page = { index: dataSourceOptions.query.page.index, size: dataSourceOptions.query.page.size };
            }

            if (queryOptions?.order || dataSourceOptions?.query?.order) {
                orders = [];
                if (queryOptions && queryOptions.order) {
                    queryOptions.order.forEach((_order) => {
                        orders.push({ desc: _order.desc, value: getOrderValue(_order) });
                    });
                } else if (dataSourceOptions && dataSourceOptions.query && dataSourceOptions.query.order) {
                    dataSourceOptions.query.order.forEach((_order) => {
                        orders.push({ desc: _order.desc, value: getOrderValue(_order) });
                    });
                }
            }

            const hasQuerySelects = !!(queryOptions && queryOptions.selects);
            const hasDefaultSelects = !!(dataSourceOptions && dataSourceOptions.query && dataSourceOptions.query.selects);

            if (hasQuerySelects || hasDefaultSelects) {
                selects = Object.assign(
                    {},
                    (dataSourceOptions && dataSourceOptions.query && dataSourceOptions.query.selects),
                    (queryOptions && queryOptions.selects)
                );
            }
            selects = getSelectsFromExpands(expands, selects);
            resolve({
                filters: filters,
                selects: selects,
                orders: orders,
                page: page
            });
        } else {
            resolve({
                filters: filters,
                selects: selects,
                orders: orders,
                page: page
            });
        }
    });
}

export interface IInwinkEntityUpdateSourceOptions extends IInwinkEntityUpdateOptions {
    idPropName?: string;
}

export function getEntityDataSource<T>(
    proxy: IAPIProxy,
    rootPath: string,
    entityRoute: string,
    entityName: string,
    userpreferencesProvider?: (datasource) => void,
    dataSourceOptions?: IDataSourceV3Options<T>
): IDataSourceV3<T> {
    const rootUrl = combine(rootPath, entityRoute);
    let continuationToken = '';

    function getTemplateFor(entityname) {
        return proxy.getJson(combine(rootPath, `${entityname}/worktemplate`)).then((res) => {
            return res as Entities.IEntityTemplateV3;
        });
    }

    function loadChildTemplates(path: string, template: Entities.IEntityTemplateV3) {
        const fields = Object.keys(template.fields);
        const promises = fields.map((fieldKey) => {
            const field = template.fields[fieldKey];
            const fieldPath = path ? `${path}.${fieldKey}` : fieldKey;
            const rootLevel = !path;
            const canProcess = (dataSourceOptions?.expandWorkTemplates
                && typeof dataSourceOptions?.expandWorkTemplates === 'object'
                && dataSourceOptions?.expandWorkTemplates[fieldPath])
                || (rootLevel && dataSourceOptions?.expandWorkTemplates === true);
            if (canProcess) {
                if ((field.type === "Entity" || field.type === "Entities") && field.value) {
                    const fv = (field.value as string).toLowerCase();
                    const customWorkTemplateProvider = dataSourceOptions?.customWorkTemplateProviders
                        && dataSourceOptions.customWorkTemplateProviders[fv];
                    return ((customWorkTemplateProvider && customWorkTemplateProvider())
                        || getTemplateFor(fv))
                        .then((childtemplate) => {
                            (field as any).template = childtemplate;
                            const childkey = path ? path + "." + field.key : field.key;
                            return loadChildTemplates(childkey, childtemplate);
                        }, () => {
                            console.error("cannot get child template for " + fv);
                        });
                }
            }
            return null;
        }).filter((f) => !!f);

        if (promises.length) {
            return Promise.all(promises).then(() => {
                return template;
            });
        }

        return Promise.resolve(template);
    }

    const _query = (path: string, queryOptions?: IInwinkEntityV3QueryOptions, expands?, forcePaging = true) => {
        if (forcePaging && (!queryOptions || !queryOptions.page || !queryOptions.page.size)) {
            return _getAll(path, queryOptions, expands);
        }
        return getQueryArgs(dataSourceOptions, queryOptions, expands).then((args) => {
            const url = _computeQueryUrl(path, queryOptions);
            const payload: IQueryArgs & { continuationToken?: string } = {
                filters: args.filters,
                selects: args.selects,
                orders: args.orders,
                page: args.page
            };

            if (queryOptions?.continuationTokenMode || dataSourceOptions?.query?.continuationTokenMode) {
                if (args.page?.index === 0) {
                    continuationToken = "";
                }
                if (continuationToken) {
                    payload.continuationToken = continuationToken;
                }
            }
            const headers = Object.assign({}, defaultPostHeaderForJsonData.headers, queryOptions?.headers);
            return proxy.postJson(url, JSON.stringify(payload), { headers }).then((res: any) => {
                continuationToken = res.continuationToken;
                return res;
            });
        });
    };

    const _computeQueryUrl = (path: string, queryOptions: IInwinkEntityV3QueryOptions) => {
        const url = rootUrl + path;

        const params = [];
        const totalCount = queryOptions?.totalCount;
        if (totalCount) {
            params.push('totalCount=' + totalCount);
        }

        if (queryOptions?.queryString) {
            params.push(queryOptions.queryString);
        }

        if (params.length === 0) {
            return url;
        }

        return `${url}${url.includes("?") ? "&" : "?"}${params.join("&")}`;
    };

    const _getAll = (path: string, queryOptions?: IInwinkEntityV3QueryOptions, expands?) => {
        return getQueryArgs(dataSourceOptions, queryOptions, expands).then((qargs) => {
            const _args = qargs;
            _args.page = _args.page || { index: 0, size: 200 };
            if (!_args.page.size || _args.page.size > 300) {
                _args.page.size = 300;
            }

            if (!_args.page.index) {
                _args.page.index = 0;
            }

            const url = _computeQueryUrl(path, queryOptions);

            const result: IInwinkEntityQueryResult<T> = {
                data: []
            };

            const queryBucket = (queryargs: IQueryArgs & { $url: string }): Promise<void> => {
                const { $url, ...args } = queryargs;
                const payload: any = { ...args };

                if (queryOptions?.continuationTokenMode || dataSourceOptions?.query?.continuationTokenMode) {
                    if (args.page?.index === 0) {
                        continuationToken = "";
                    }
                    if (continuationToken) {
                        payload.continuationToken = continuationToken;
                    }
                }

                const headers = Object.assign({}, defaultPostHeaderForJsonData.headers, queryOptions?.headers);
                return proxy.postJson(url, JSON.stringify(payload), { headers })
                    .then((res: IInwinkEntityQueryResult<T> & { continuationToken?: string }) => {
                        continuationToken = res.continuationToken;
                        if (res.data && res.data.length) {
                            result.data.push(...res.data);
                            if (res.data.length === queryargs.page.size) {
                                const newArgs = Object.assign({}, queryargs, {
                                    $url: url,
                                    page: Object.assign({}, queryargs.page, {
                                        index: queryargs.page.index + 1
                                    })
                                });
                                return queryBucket(newArgs);
                            }
                        }
                    });
            };

            return queryBucket({ $url: url, ..._args }).then(() => {
                return result;
            });
        });
    };

    const ds: IDataSourceV3<T> = {
        entityName: entityName,
        options: dataSourceOptions,
        customColumns: [],
        excludeColumns: [],
        isV3: true,
        clone(newQueryOptions?: IDataSourceV3Options<T>): IDataSourceV3<T> {
            return getEntityDataSource<T>(proxy, rootPath, entityRoute,
                entityName, userpreferencesProvider, newQueryOptions ?? dataSourceOptions);
        },
        // setQueryOptions(newQueryOptions: IDataSourceV3Options) {
        //     dataSourceOptions = newQueryOptions;
        // },
        getById(entityid) {
            return proxy.getJson(rootUrl + '/' + entityid);
        },
        import(entities) {
            return proxy.postJson(rootUrl + '/import', JSON.stringify(entities), defaultPostHeaderForJsonData);
        },
        importPreview(query: IImportPreviewQuery<T>) {
            return proxy.postJson(rootUrl + '/import/preview', JSON.stringify(query), defaultPostHeaderForJsonData);
        },
        get(entityid, options: IInwinkEntityV3QueryOptions, _idpropname?: string, expands?: any[]) {
            let idpropname = _idpropname;
            if (!idpropname) {
                idpropname = "id";
            }
            const updatedoptions = Object.assign({}, options, {
                filters: Object.assign({}, options && options.filters, {
                    [idpropname]: entityid
                }),
                page: {
                    size: 1
                }
            });

            return this.query(updatedoptions, expands).then((res) => {
                return res.data && res.data[0];
            });
        },
        update(_entity, options?: IInwinkEntityUpdateSourceOptions): Promise<T> {
            let entity = _entity;
            if (dataSourceOptions && dataSourceOptions.beforeUpdate) {
                entity = dataSourceOptions.beforeUpdate(entity);
            }

            const payload = Object.assign({}, options, {
                entity: entity
            });

            const idPropName = options?.idPropName || 'id';
            const id = entity[idPropName];
            let url = rootUrl;
            if (id) {
                url += '/' + id;
                return proxy.postJson(url, JSON.stringify(payload), defaultPostHeaderForJsonData);
            } else {
                return proxy.patchJson(url, JSON.stringify(payload), defaultPostHeaderForJsonData);
            }
        },
        create(_entity, options?: IInwinkEntityUpdateOptions): Promise<T> {
            let entity = _entity;
            if (dataSourceOptions && dataSourceOptions.beforeCreate) {
                entity = dataSourceOptions.beforeCreate(entity);
            }
            const payload = Object.assign({}, options, {
                entity: entity
            });
            return proxy.postJson(rootUrl, JSON.stringify(payload), defaultPostHeaderForJsonData);
        },
        count(queryOptions?: IInwinkEntityV3QueryOptions) {
            const filters = getFilters(dataSourceOptions, queryOptions);
            let url = rootUrl + "/count";
            if (queryOptions?.queryString) {
                const paramQuery = url.includes("?") ? "&" : "?";
                url = url + paramQuery + queryOptions.queryString;
            }
            return proxy.postJson(url, JSON.stringify({ filters }), defaultPostHeaderForJsonData);
        },
        getAll(queryOptions?: IInwinkEntityV3QueryOptions, expands?): Promise<IInwinkEntityQueryResult<T>> {
            return _getAll("/query", queryOptions, expands);
        },
        queryHistory(queryOptions?: IInwinkEntityV3QueryOptions, expands?): Promise<IInwinkEntityQueryResult<T>> {
            return _query("/history/query", queryOptions, expands);
        },
        queryRemoved(queryOptions?: IInwinkEntityV3QueryOptions, expands?): Promise<IInwinkEntityQueryResult<T>> {
            return _query("/history/removed/query", queryOptions, expands);
        },
        countRemoved(queryOptions?: IInwinkEntityV3QueryOptions) {
            const filters = getFilters(dataSourceOptions, queryOptions);
            return proxy.postJson(rootUrl + "/history/removed/count", JSON.stringify({ filters }), defaultPostHeaderForJsonData);
        },
        query(queryOptions?: IInwinkEntityV3QueryOptions, expands?): Promise<IInwinkEntityQueryResult<T>> {
            return _query("/query", queryOptions, expands);
        },
        queryCall(path: string, queryOptions?: IInwinkEntityV3QueryOptions, expands?): Promise<IInwinkEntityQueryResult<T>> {
            return _query(path, queryOptions, expands, false);
        },
        massDelete(queryOptions?: IInwinkEntityV3QueryOptions, directDelete = false): Promise<IInwinkEntityQueryResult<T>> {
            const direct = dataSourceOptions?.forceDirectMassDelete ? true : directDelete;
            return getQueryArgs(dataSourceOptions, queryOptions).then((args) => {
                const url = `${rootUrl}/massdelete?direct=${direct}`;
                return proxy.postJson(url, JSON.stringify({
                    filters: args.filters,
                }), defaultPostHeaderForJsonData);
            });
        },
        delete(ids: string[], directDelete = false, customPayload?: any): Promise<any> {
            const direct = dataSourceOptions?.forceDirectMassDelete ? true : directDelete;
            if ((ids && ids.length === 1) || (customPayload && customPayload.length === 1)) {
                const body = { entity: {} };
                if (ids) {
                    body.entity = { id: ids[0] };
                } else {
                    body.entity = customPayload[0].entity;
                }
                // delete one entity
                return proxy.postJson(`${rootUrl}/delete`, JSON.stringify(body), defaultPostHeaderForJsonData);
            } if (ids && ids.length > 0) {
                // Delete multple entity
                const filters = { id: ids };
                return proxy.postJson(`${rootUrl}/massdelete?direct=${direct}`,
                    JSON.stringify({ filters }), defaultPostHeaderForJsonData);
            } if (!ids && customPayload && customPayload.length > 1) {
                const queryOptions: IInwinkEntityV3QueryOptions = {
                    expression: {
                        or: customPayload.map((item) => {
                            return {
                                and: Object.keys(item.entity).map((key) => ({
                                    name: key,
                                    op: "eq",
                                    val: item.entity[key]
                                }))
                            };
                        })
                    }
                };
                return getQueryArgs(dataSourceOptions, queryOptions).then((args) => {
                    const url = `${rootUrl}/massdelete?direct=${direct}`;
                    return proxy.postJson(url, JSON.stringify({
                        filters: args.filters,
                    }), defaultPostHeaderForJsonData);
                });
            }

            return Promise.reject(new Error("not implemented"));
        },
        entitytemplate(includeStaticFields: boolean, includeIdField = false): Promise<Entities.IEntityTemplate> {
            return ds.worktemplate().then((template: Entities.IEntityTemplateV3) => {
                return workTemplateToV2(template, includeIdField);
            });
        },
        worktemplate(forceReload?: boolean, disableExpandWorkTemplates?: boolean): Promise<Entities.IEntityTemplateV3> {
            const data = ds as any;

            if (!forceReload && data.cachedWorkTemplate) {
                return data.cachedWorkTemplate;
            }

            const promise = getTemplateFor(entityRoute).then((template: Entities.IEntityTemplateV3) => {
                if (disableExpandWorkTemplates !== true && dataSourceOptions?.expandWorkTemplates) {
                    return loadChildTemplates("", template);
                }

                return template;
            }).then((template) => {
                return template;
            });

            data.cachedWorkTemplate = promise.then((res) => {
                return res;
            }, (err) => {
                data.cachedWorkTemplate = null;
                return Promise.reject(err);
            });

            return promise;
        },
        updateentitytemplate(template): Promise<any> {
            const tmp = Object.assign({}, template, {
                // on ne sauvegarde pas les champs de type inherited
                fields: template.fields.filter((f) => f.inherited !== true)
            });
            return proxy.postJson(rootUrl + '/updateentitytemplate', JSON.stringify(tmp), defaultPostHeaderForJsonData);
        },
        exportV3(query: IExportV3Payload, direct = false) {
            const drct = dataSourceOptions?.forceDirectExport ? true : direct;
            const payload = {
                filters: getFilters(dataSourceOptions, query),
                columns: query.columns,
                selects: getSelectsFromExpands(query.expands, query.selects),
                orders: [],
                title: query.title || null
            };

            if (query.order) {
                query.order.forEach((_order) => {
                    payload.orders.push({ desc: _order.desc, value: explode(_order.by, {}) });
                });
            }

            return proxy.postJson(`${rootUrl}/export/excel?direct=${drct ? "true" : "false"}`, JSON.stringify(payload), {
                headers: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    "Content-Type": "application/json"
                }
            });
        },
        exportZipFile(query: IExportZipFilePayload, direct = false) {
            const payload: any = {
                filters: getFilters(dataSourceOptions, query),
                files: query.files,
                selects: query.selects || null
            };

            return proxy.postJson(`${rootUrl}/export/files?direct=${direct ? "true" : "false"}`, JSON.stringify(payload), {
                headers: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    "Content-Type": "application/json"
                }
            });
        },
        exportPdfFile(query: IExportPdfFilePayload, direct = false) {
            const payload = {
                filters: getFilters(dataSourceOptions, query),
                orders: query.order?.length ? query.order.map((o) => ({ desc: o.desc, value: getOrderValue(o) })) : [],
                fields: query.fields,
                title: query.title || null,
                selects: query.selects
            };

            return proxy.postJson(`${rootUrl}/export/pdf?direct=${direct}`, JSON.stringify(payload), {
                headers: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    "Content-Type": "application/json"
                }
            });
        },
        massUpdate(input: Partial<T>, filters: { filters: any, where: any, expression: any }, direct = false,
            populateWithMergeArray = false): Promise<any> {

            const populatewithmergearrayQueryString = `&populatewithmergearray=${populateWithMergeArray ? "true" : "false"}`;

            return this.worktemplate().then((worktemplate) => {
                const payload = {
                    input,
                    filters: {
                        filters: {
                            ...getFilters(dataSourceOptions, filters)
                        },
                    },
                };

                if (worktemplate?.fields && Object.keys(worktemplate.fields)?.some((k) => k === "isAnonymized")) {
                    payload.filters.filters = {
                        ...payload.filters.filters,
                        isAnonymized: { $equal: false }
                    };
                }

                return proxy.postJson(
                    `${rootUrl}/massupdate?direct=${direct ? "true" : "false"}${populatewithmergearrayQueryString}`,
                    JSON.stringify(payload),
                    {
                        headers: {
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            "Content-Type": "application/json"
                        }
                    });
            });

        },
        getDocumentUrl(entityid: string, templateid: string, lang: string): Promise<any> {
            return proxy.getJson(`${rootUrl}/${entityid}/doc/${templateid}?direct=true&lang=${lang}`);
        }
    };

    if (userpreferencesProvider) {
        userpreferencesProvider(ds);
    }

    return ds;
}

export function workTemplateToV2(template: Entities.IEntityTemplateV3, includeIdField = false) {
    if (!template) {
        return null;
    }
    const entitytemplate: Entities.IEntityTemplate = {
        languages: null,
        scopes: template.scopes,
        oldKeys: template.oldKeys,
        fields: Object
            .keys(template.fields)
            .filter((key) => {
                let _r = false;
                if (includeIdField && key === "id") {
                    _r = true;
                } else if (template.fields[key] && (template.fields[key].metadata)) {
                    _r = !template.fields[key].metadata.isTechnical;
                }

                return _r;
            })
            .map((key) => {
                const field = template.fields[key];
                const res = fieldV3toV2(field);

                return res;
            })
    };

    return entitytemplate;
}
export function fieldV3toV2(field: Entities.IEntityFieldTemplateV3) {
    let nestedEntity: Record<string, Entities.IEntityFieldTemplate> = null;
    if (field.nestedEntity) {
        nestedEntity = {};
        Object.values(field.nestedEntity).forEach((f) => {
            nestedEntity[f.key] = fieldV3toV2(f);
        });
    }

    const res: Entities.IEntityFieldTemplate = {
        key: field.key,
        type: field.type,
        canBeNull: field.metadata?.canBeNull,
        defaultDisplay: field.display && field.display.defaultDisplay,
        labels: field.display && field.display.labels,
        descriptions: field.display && field.display.descriptions,
        placeholders: field.display && field.display.placeholders,
        remarks: field.display && field.display.remarks,
        tooltip: field.display && field.display.tooltip,
        isStatic: field.metadata && field.metadata.isStatic,
        isLocalizable: field.metadata && field.metadata.isLocalizable,
        isLocked: field.metadata && field.metadata.isLocked,
        applyTo: field.metadata && field.metadata.applyTo,
        isMandatory: field.validation && field.validation.isMandatory,
        isMandatoryFor: field.validation && field.validation.isMandatoryFor,
        min: field.validation && field.validation.min,
        max: field.validation && field.validation.max,
        nbDecimalLimit: field?.validation?.nbDecimalLimit,
        value: field.value,
        valuesList: field.valuesList,
        valuesListSort: field.display && field.display.valuesListSort,
        isPublicFile: field.file && field.file.isPublicFile,
        mimeTypes: field.file && field.file.mimeTypes,
        maxSize: field.file && field.file.maxSize,
        recommandedAspectRatios: field.file?.recommandedAspectRatios,
        aspectRatio: field.file?.aspectRatio,
        isCollection: field.metadata && field.metadata.isCollection,
        regex: field?.validation?.regex,
        isReadOnly: field.metadata?.readOnly,
        isReadOnlyOnUpdate: field.metadata?.readOnlyOnUpdate,
        isPersonalData: field.metadata?.isPersonalData,
        aiAssistantContext: field.metadata?.aiAssistantContext
    };

    if (field.metadata?.inherited) {
        res.inherited = true;
        res.origin = field.metadata.origin;
    }

    if (nestedEntity) {
        res.nestedEntity = nestedEntity;
    }
    return res;
}
